// The pure shapes of the Desk Dashboard panels.
//
// Each input is a response that the server scopes to the book of the signed-in
// investment manager. No function here reads another book.

import type { CoacActionRow, DeskDashboardBreach, DeskDashboardClientRow } from '@septima/contract';
import { isOpen } from '@/lib/coac/lifecycle';
import { daysUntil } from '@/lib/clock';

// --- The breach flags of a client row -----------------------------------------

/** The flags that a row of "Your clients" shows. */
export interface ClientBreachFlags {
    /** An open risk alert, or equities above the limit of the profile. */
    overRisk: boolean;
    /** An open deviation from the house model, or a class above the limit of the profile. */
    allocation: boolean;
    /** The statement of each risk breach, for the hover text of the flag. */
    overRiskDetail: string[];
    /** The statement of each allocation breach, for the hover text of the flag. */
    allocationDetail: string[];
}

/** The breach flags of each client that has a breach. A client with none is absent. */
export function clientBreachFlags(
    breaches: readonly DeskDashboardBreach[],
): Map<string, ClientBreachFlags> {
    const out = new Map<string, ClientBreachFlags>();
    for (const b of breaches) {
        const flags = out.get(b.clientId) ?? {
            overRisk: false,
            allocation: false,
            overRiskDetail: [],
            allocationDetail: [],
        };
        if (b.kind === 'risk' || b.kind === 'risk_limit') {
            flags.overRisk = true;
            flags.overRiskDetail.push(b.summary);
        } else {
            flags.allocation = true;
            flags.allocationDetail.push(b.summary);
        }
        out.set(b.clientId, flags);
    }
    return out;
}

/**
 * The flag filter of "Your clients". `flagged` keeps a client with any flag.
 * `null` keeps every client.
 */
export type ClientFlagFilter = 'flagged' | 'overRisk' | 'allocation' | null;

/** True when the flags of a client pass the flag filter. */
export function matchesFlagFilter(
    flags: ClientBreachFlags | undefined,
    filter: ClientFlagFilter,
): boolean {
    switch (filter) {
        case null:
            return true;
        case 'flagged':
            return !!flags && (flags.overRisk || flags.allocation);
        case 'overRisk':
            return !!flags?.overRisk;
        case 'allocation':
            return !!flags?.allocation;
    }
}

// --- Clients against their ARC band --------------------------------------------

export interface ArcGapRow {
    client: DeskDashboardClientRow;
    /** The client return less the ARC return of the band. Null when a side is absent. */
    gapPct: number | null;
}

/**
 * Every client in view, with the gap to the ARC band. The largest shortfall
 * comes first. A client without both figures goes last, in name order, so the
 * list keeps each client of the book.
 */
export function arcGapRows(clients: readonly DeskDashboardClientRow[]): ArcGapRow[] {
    return clients
        .map((client) => ({
            client,
            gapPct:
                client.rangeReturnPct != null && client.benchmarkRangeReturnPct != null
                    ? client.rangeReturnPct - client.benchmarkRangeReturnPct
                    : null,
        }))
        .sort((a, b) => {
            if (a.gapPct == null && b.gapPct == null) {
                return a.client.fullName.localeCompare(b.client.fullName);
            }
            if (a.gapPct == null) return 1;
            if (b.gapPct == null) return -1;
            return a.gapPct - b.gapPct;
        });
}

// --- Corporate action deadlines ------------------------------------------------

export interface DeadlineRow {
    id: string;
    label: string;
    eventType: string;
    /** The internal response deadline (SEP-201). */
    deadline: string;
    /** Days from today to the deadline. A negative value is past the deadline. */
    days: number;
}

/** Every open corporate action of the book that has a deadline, soonest first. */
export function deadlineRows(actions: readonly CoacActionRow[]): DeadlineRow[] {
    return actions
        .filter((a) => a.responseDeadline && !a.isDeleted && isOpen(a))
        .map((a) => {
            const deadline = a.responseDeadline as string;
            return {
                id: a.id,
                label: a.securityName ?? a.eventType,
                eventType: a.eventType,
                deadline,
                days: daysUntil(deadline),
            };
        })
        .sort((a, b) => a.days - b.days || a.deadline.localeCompare(b.deadline));
}

// --- Days left -------------------------------------------------------------------

/** The text colour of the days left: red within two days, amber within seven. */
export function daysLeftClass(days: number): string {
    if (days <= 2) return 'text-[rgb(var(--color-rag-red))]';
    if (days <= 7) return 'text-[rgb(var(--color-rag-amber))]';
    return 'text-[rgb(var(--color-text-secondary))]';
}

/** The days left in words: "3d overdue", "Today" or "12d". */
export function daysLeftLabel(days: number): string {
    if (days < 0) return `${-days}d overdue`;
    if (days === 0) return 'Today';
    return `${days}d`;
}
